import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";

import { EnvMicroDocument } from "../config/env.micro.document";
import { DOCUMENT_ENV } from "../config/micros-env.tokens";
import { SessionCtl } from "./session.ctl.service";
import { ChatMediaUrlPolicy, toRawMediaReference } from "./chat-media-url-policy.service";

const DOWNLOAD_URL_PATH = "/download-url";

/**
 * Descarga autenticada de adjuntos de chat (imágenes, PDF, Word, Excel, etc).
 *
 * Antes esto hacía un `HttpClient.get` como blob contra `GET /api/v1/s3`, que
 * responde 302 hacia una URL presignada de S3. El XHR seguía ese redirect
 * cross-origin de forma transparente y moría por CORS: el bucket no expone
 * `Access-Control-Allow-Origin` para GET, así que la petición terminaba con
 * `status: 0`.
 *
 * Ahora se pide la URL presignada al endpoint `GET /api/v1/s3/download-url`,
 * que la devuelve como JSON (200) en vez de redirigir. Esa URL viene firmada
 * con `Content-Disposition: attachment`, de modo que basta con navegar a ella
 * desde un anchor plano: una navegación no aplica CORS, S3 fuerza la descarga
 * a disco, y se conserva el soporte nativo de Range/206 que necesita el vídeo.
 *
 * El endpoint 302 original se mantiene intacto porque es el que alimenta el
 * render inline de `<img>` y `<video>`.
 */
@Injectable({ providedIn: "root" })
export class ChatFileDownloadService {
  constructor(
    private readonly http: HttpClient,
    private readonly sessionCtl: SessionCtl,
    @Inject(DOCUMENT_ENV) private readonly documentEnv: EnvMicroDocument,
    private readonly mediaUrlPolicy: ChatMediaUrlPolicy,
  ) {}

  async downloadFile(fileUrl: string | null | undefined): Promise<void> {
    const sourceUrl = this.mediaUrlPolicy.resolvePersisted(fileUrl);
    if (!sourceUrl) return;

    const endpointUrl = this.buildDownloadUrlEndpoint(sourceUrl);
    if (!endpointUrl) return;

    try {
      const response = await firstValueFrom(
        this.http.get<unknown>(endpointUrl, {
          headers: new HttpHeaders({
            Authorization: `Bearer ${this.sessionCtl.getAccessTokenOrThrow()}`,
          }),
        }),
      );

      const presignedUrl = readPresignedUrl(response);
      if (!presignedUrl) {
        console.error(
          "[ChatFileDownloadService] Malformed download-url response",
        );
        return;
      }

      // Validación obligatoria antes de tocar `anchor.href`: sin esto, una
      // respuesta comprometida o defectuosa del backend se convierte en un
      // open redirect o en un sink de `javascript:`. La política exige https,
      // rechaza credenciales embebidas y limita el origen a la lista blanca.
      const safeUrl = this.mediaUrlPolicy.resolveAbsolute(presignedUrl);
      if (!safeUrl) {
        console.error("[ChatFileDownloadService] Rejected download-url origin");
        return;
      }

      const anchor = document.createElement("a");
      anchor.href = safeUrl;
      anchor.rel = "noopener noreferrer";
      // No se asigna `anchor.download`: el navegador lo ignora en URLs
      // cross-origin. Quien fuerza la descarga es el `Content-Disposition:
      // attachment` que el backend firma en la URL presignada. Añadirlo aquí
      // no aporta nada y sugiere un control sobre el nombre que no existe.
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch (error) {
      console.error("[ChatFileDownloadService] Download failed", error);
    }
  }

  private buildDownloadUrlEndpoint(fileUrl: string): string | null {
    const baseUrl = `${trimTrailingSlash(this.documentEnv.host)}${ensureLeadingSlash(this.documentEnv.apiBasePath)}`;
    // `toRawMediaReference` deshace el percent-encoding que el messaging-ms le
    // metió al path: sin eso, `encodeURIComponent` codifica el `%` otra vez y el
    // backend recibe `%2520` en vez del espacio real de la key de S3.
    return this.mediaUrlPolicy.resolvePersisted(
      `${baseUrl}${DOWNLOAD_URL_PATH}?fileUrl=${encodeURIComponent(toRawMediaReference(fileUrl))}`,
    );
  }
}

/**
 * El backend responde con el sobre estándar `{ status, result, data }`, donde
 * `data` es la URL. Se acepta también la cadena pelada por robustez.
 */
function readPresignedUrl(response: unknown): string | null {
  if (typeof response === "string") return response;
  if (!response || typeof response !== "object") return null;

  const data = (response as { data?: unknown }).data;
  return typeof data === "string" ? data : null;
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

function ensureLeadingSlash(value: string): string {
  return value.startsWith("/") ? value : `/${value}`;
}
